import React, { useRef, useState } from 'react';
import StaffEditor from './StaffEditor';
import PersonnelPdfButton from './PersonnelPdfButton';
import { compactTime, dayLongLabel } from './AgendaResponsiveView';
import useSwipeBack from '../../hooks/useSwipeBack';

function normalizeTimeText(value) {
  const raw = String(value || '').trim().replace('.', ':');
  if (!raw) return '';
  if (/^\d{1,2}$/.test(raw)) return `${String(Math.min(23, Math.max(0, Number(raw)))).padStart(2, '0')}:00`;
  const match = raw.match(/^(\d{1,2}):(\d{1,2})$/);
  if (!match) return raw;
  return `${String(Math.min(23, Math.max(0, Number(match[1])))).padStart(2, '0')}:${String(Math.min(59, Math.max(0, Number(match[2])))).padStart(2, '0')}`;
}

function operationDate(item) {
  return item?.operation_date?.slice(0, 10) || item?.armado_date?.slice(0, 10) || '';
}

function operationKind(item) {
  return item?.type === 'desarmado' ? 'desarmado' : 'armado';
}

export default function AgendaMobileOperationEditor({
  item,
  canEdit,
  canEditAssignments,
  personnel,
  assignmentModel,
  assignmentModelError,
  retryAssignmentModel,
  onCancel,
  onSave,
}) {
  const staffTabGroup = useRef({});
  const type = operationKind(item);
  const isOperation = item?.entity_type === 'operation' || item?.operation_date;
  const [form, setForm] = useState({
    date: operationDate(item),
    title: item?.title || item?.event_name || (type === 'desarmado' ? 'Desarmado' : 'Armado'),
    place: item?.place || item?.event_place || '',
    start_time: compactTime(item?.start_time),
    end_time: compactTime(item?.end_time),
    note: item?.note || item?.notes || '',
    status: item?.status || 'pendiente',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const disabled = !canEdit || saving;

  function setField(key) {
    return event => setForm(prev => ({ ...prev, [key]: event.target.value }));
  }

  async function submit() {
    setSaving(true);
    setError('');
    try {
      await onSave(item, {
        ...form,
        operation_date: isOperation ? form.date : undefined,
        armado_date: !isOperation ? form.date : undefined,
        start_time: normalizeTimeText(form.start_time) || null,
        end_time: normalizeTimeText(form.end_time) || null,
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  }

  const swipeBack = useSwipeBack(onCancel);

  return (
    <main className="agm-editor-shell" {...swipeBack}>
      <header className="agm-editor-header">
        <div className="agm-editor-top">
          <button type="button" className="agm-back-btn" onClick={onCancel}>‹</button>
          <span className="agm-editor-top-spacer" aria-hidden="true" />
          {canEdit && <button type="button" className="agm-save-btn" onClick={submit} disabled={saving}>{saving ? 'Guardando' : 'Guardar'}</button>}
        </div>
        <div className="agm-editor-title-block">
          <p>{form.date ? dayLongLabel(form.date) : 'Sin fecha'} · {type === 'desarmado' ? 'Desarmado' : 'Armado'}</p>
          <textarea
            className="agm-editor-title-input"
            value={form.title}
            onChange={setField('title')}
            disabled={disabled}
            placeholder="Titulo"
            rows={2}
          />
        </div>
      </header>

      <section className="agm-editor-card agm-editor-info">
        <div className="agm-editor-line agm-editor-line--two">
          <label>
            <span>Fecha</span>
            <input type="date" value={form.date} onChange={setField('date')} disabled={disabled} />
          </label>
          <label>
            <span>Estado</span>
            <select value={form.status} onChange={setField('status')} disabled={disabled}>
              <option value="pendiente">Pendiente</option>
              <option value="finalizado">Finalizado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </label>
        </div>

        <div className="agm-editor-line agm-editor-line--two">
          <label>
            <span>Inicio</span>
            <input inputMode="numeric" placeholder="HH:MM" value={form.start_time} onChange={setField('start_time')} disabled={disabled} />
          </label>
          <label>
            <span>Fin</span>
            <input inputMode="numeric" placeholder="HH:MM" value={form.end_time} onChange={setField('end_time')} disabled={disabled} />
          </label>
        </div>

        <label>
          <span>Lugar</span>
          <input value={form.place} onChange={setField('place')} disabled={disabled} placeholder="Lugar" />
        </label>

        <label>
          <span>Notas</span>
          <textarea value={form.note} onChange={setField('note')} disabled={disabled} placeholder="Notas..." />
        </label>
      </section>

      <section className="agm-editor-card agm-editor-assignments">
        <div className="agm-editor-card-head">
          <h2>Asignaciones</h2>
          <PersonnelPdfButton entityType={isOperation ? 'operation' : 'armado'} entityId={item.id} />
        </div>
        <StaffEditor
          operationId={isOperation ? item.id : null}
          armadoId={!isOperation ? item.id : null}
          initialAssignments={item.staff_assignments || []}
          initialSections={item.staff_sections || []}
          assignmentModel={assignmentModel}
          assignmentModelError={assignmentModelError}
          retryAssignmentModel={retryAssignmentModel}
          personnel={personnel || []}
          canEdit={canEditAssignments}
          tabGroup={staffTabGroup}
          tabIndexStart={20}
          entityDate={form.date}
          startTime={form.start_time}
          endTime={form.end_time}
          touchPicker
        />
      </section>

      {error && <p className="agm-editor-error">{error}</p>}
    </main>
  );
}
